import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          throw new Error('No session_id found');
        }

        // Exchange session_id for our session
        const response = await axios.post(
          `${API_URL}/api/portal/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        const user = response.data;

        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);

        // Redirect to appropriate dashboard based on role
        if (user.role === 'investor') {
          navigate('/portal/investor', { state: { user }, replace: true });
        } else if (user.role === 'company') {
          navigate('/portal/company', { state: { user }, replace: true });
        } else {
          navigate('/portal', { state: { user }, replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Authentication failed';
        
        // Check if it's a pending approval message
        if (errorMessage.includes('pending approval') || errorMessage.includes('pending')) {
          setError('pending');
        } else {
          setError(errorMessage);
        }
      }
    };

    processAuth();
  }, [navigate]);

  if (error) {
    // Special case for pending approval
    if (error === 'pending') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#1A3A4A] to-[#0f252e] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-amber-600 text-3xl">⏳</span>
            </div>
            <h2 className="font-heading text-2xl font-semibold text-[#1A3A4A] mb-2">
              Account Pending Approval
            </h2>
            <p className="font-body text-[#1A3A4A]/70 mb-6">
              Your account has been created and is waiting for approval. We'll notify you by email once your account is activated.
            </p>
            <div className="p-4 bg-[#F8FAFB] rounded-xl mb-6">
              <p className="text-sm text-[#1A3A4A]/60">
                This usually takes 1-2 business days.
              </p>
            </div>
            <button
              onClick={() => navigate('/portal/login')}
              className="bg-[#1A3A4A] text-white px-6 py-3 rounded-full font-body font-medium hover:bg-[#1A3A4A]/90 transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">!</span>
          </div>
          <h2 className="font-heading text-xl font-semibold text-[#1A3A4A] mb-2">
            Access Denied
          </h2>
          <p className="font-body text-[#1A3A4A]/70 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/portal/login')}
            className="bg-[#1A3A4A] text-white px-6 py-3 rounded-full font-body font-medium hover:bg-[#1A3A4A]/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#60B5AF] animate-spin mx-auto mb-4" />
        <p className="font-body text-[#1A3A4A]/70">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
